import { useCallback } from 'react';
import { components } from '@api/club/generated';

import { CapsuleButton } from '@ui/components/CapsuleButton';
import { Placeholder } from '@ui/components/Placeholder/Placeholder';
import { SearchInput } from '@ui/components/SearchInput';
import { Table, TableCell, TableRow } from '@ui/components/Table';
import { ThemeButton } from '@ui/components/ThemeButton';

import { RcfAttributes } from '@features/shared/api/typings';

import styles from './RcfUserSearch.module.css';

type Props = {
  query: string;
  onQueryChange: (newQuery: string) => void;
  isLoading: boolean;
  rcfUsers: components['schemas']['RcfRatingDto'][];
  onSetRcfAttributes: (newAttributes: Partial<RcfAttributes>) => void;
  hasNextPage: boolean;
  fetchNextPage: () => void;
};
export function RcfUserSearch({
  query,
  onQueryChange,
  isLoading,
  rcfUsers,
  onSetRcfAttributes,
  hasNextPage,
  fetchNextPage,
}: Props) {
  return (
    <div className={styles.searchForm}>
      <SearchInput
        isLoading={isLoading}
        placeholder="Поиск игрока в базе ФШР"
        onQueryChange={onQueryChange}
        query={query}
      />

      <div className={styles.scrollable}>
        <Table className={styles.users}>
          <TableRow header>
            <TableCell slim>ФШР&nbsp;ID</TableCell>
            <TableCell slim leftGap>
              Имя
            </TableCell>
            <TableCell slim leftGap>
              Год рождения
            </TableCell>
            <TableCell slim leftGap>
              Пол
            </TableCell>
            <TableCell left leftGap slim>
              Рейтинг
            </TableCell>
            <TableCell left leftGap slim></TableCell>
          </TableRow>

          {rcfUsers?.map((user) => (
            <RcfUserRow
              key={user.rcfId + user.fullName}
              rcfUser={user}
              onSetRcfAttributes={onSetRcfAttributes}
            />
          ))}
        </Table>

        {(!rcfUsers || !rcfUsers.length) && !query && !isLoading && (
          <Placeholder>Для поиска введите имя или ФШР ID игрока</Placeholder>
        )}

        {(!rcfUsers || !rcfUsers.length) && query && !isLoading && (
          <Placeholder>Не удалось найти игроков</Placeholder>
        )}

        {hasNextPage && (
          <CapsuleButton
            className={styles.showMoreBtn}
            preset="hollow-golden"
            onClick={fetchNextPage}
          >
            Показать еще
          </CapsuleButton>
        )}
      </div>
    </div>
  );
}

type RcfUserRowProps = {
  rcfUser: components['schemas']['RcfRatingDto'];
  onSetRcfAttributes: (newAttributes: Partial<RcfAttributes>) => void;
};
function RcfUserRow({ rcfUser, onSetRcfAttributes }: RcfUserRowProps) {
  const handleSetRcfAttributes = useCallback(() => {
    onSetRcfAttributes({
      rcfId: rcfUser.rcfId,
    });

    /** На всякий случай пока оставлю это здесь **/
    /**
     * birthdayTimestampMs: rcfUser.yearOfBirth
     *         ? new Date(rcfUser.yearOfBirth, 0).getTime()
     *         : undefined,
     *       gender: rcfUser.gender,
     */
  }, [onSetRcfAttributes, rcfUser]);

  return (
    <TableRow key={rcfUser.rcfId}>
      <TableCell>{rcfUser.rcfId}</TableCell>
      <TableCell className={styles.nameColumn} leftGap>
        <div className={styles.name}>{rcfUser.fullName}</div>
      </TableCell>
      <TableCell leftGap>{rcfUser.yearOfBirth}</TableCell>
      <TableCell leftGap>{GENDER_RU[rcfUser.gender]}</TableCell>
      <TableCell fitContent left leftGap>
        {rcfUser.elo.blitz} · {rcfUser.elo.rapid}
      </TableCell>
      <TableCell left leftGap slim>
        <ThemeButton compact onClick={handleSetRcfAttributes}>
          Подставить
        </ThemeButton>
      </TableCell>
    </TableRow>
  );
}

const GENDER_RU = {
  Male: 'Муж.',
  Female: 'Жен.',
};
